import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import {
  API_VENDOR_TEST_LIMIT_EXCEEDED,
  type VendorApiType,
  getVendorApiDetails,
  settingTypes,
} from '@ipfabric/utils';
import { useLocale } from 'containers/App/LocaleContext';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { type FC, type JSX, type ReactNode, useState } from 'react';
import { generateGuid } from 'utils/guid';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { useApi } from 'utils/request/useApi';
import { VendorDialog } from './VendorDialog';
import { VendorsApiTable } from './VendorsApiTable';
import type { VendorDialogType } from './constants';
import { updateVendorForRequest } from './helpers/updateVendorForRequest';
import type { ExistingVendor, Vendor } from './vendorTypes';

interface Props {
  addVendorButtonText?: string;
  newSnapshot?: boolean;
  noCredentialBox: JSX.Element | null;
  onVendorsChange: (vendors: ExistingVendor[]) => void;
  showUpdateFromGlobalSettingsButton?: boolean;
  snapshotId: string | null;
  vendors: ExistingVendor[];
  vendorsError?: boolean;
  renderTitleBadge?: () => ReactNode;
}

const VendorsApi: FC<Props> = (props) => {
  const [addVendorDialogOpen, setAddVendorDialogOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<string | null>(null);
  const [testingVendor, setTestingVendor] = useLoadingState();
  const [globalStateLoading, setGlobalStateLoading] = useLoadingState();
  const useGlobalSettingsConfirmDialog = useSimpleConfirmDialog({
    btnConfirmAccent: 'negative',
    btnCancelAccent: 'positive',
  });
  const api = useApi();
  const notifications = useNotifications();
  const msg = useLocale().discoverySettings.vendorsApi;
  const selectedSnapshot = useSnapshotId();

  const getEditInitialValues = () => {
    return props.vendors.find((row) => {
      return row.id === editVendor;
    });
  };

  const getEditName = (): string => {
    const foundItem = getEditInitialValues();
    if (foundItem) {
      if (
        foundItem.type === settingTypes.vendorApiNames.azure &&
        foundItem.subscriptionIds
      ) {
        return foundItem.subscriptionIds.join(', ');
      }
      if ('baseUrl' in foundItem && typeof foundItem.baseUrl === 'string') {
        return foundItem.baseUrl;
      }
      return msg.vendorTypes[foundItem.type as VendorApiType];
    }
    return '';
  };

  const getEditType = (): VendorApiType => {
    const foundItem = getEditInitialValues();
    // getEditType() invokes when vendors.length > 0 and model.editVendor !== null
    // getEditType() is called when a user clicks on the edit button and edit dialog is displaying
    // therefore vendors would always have a vendor with id equaled to model.editVendor
    return foundItem!.type;
  };

  const notifyVerifyError = (e: unknown) => {
    const isApi = isIpfError(e, 'API');
    const resp = isApi
      ? ((e as any)?.payload?.response as { code?: string; message?: string })
      : undefined;

    if (resp?.code === API_VENDOR_TEST_LIMIT_EXCEEDED) {
      notifications.showNotification({
        title: 'Connection test not available',
        message: resp?.message,
        type: 'warning',
      });
      return;
    }

    notifications.showNotification({
      title: 'Connection failed',
      message: (isApi && resp?.message) || 'Unknown error',
      type: 'danger',
    });
  };

  const verifyConnection = useStableCallback(
    async (requestData: any, testOnly: boolean | undefined) => {
      try {
        if ('id' in requestData && requestData.id) {
          if (props.snapshotId !== null) {
            await api.request({
              url: `/snapshots/${props.snapshotId}/settings/vendor-api/verify-connection/reverify`,
              method: 'POST',
              data: requestData,
            });
          } else {
            await api.request({
              url: '/settings/vendor-api/verify-connection/reverify',
              method: 'POST',
              data: requestData,
            });
          }
        } else {
          await api.request({
            url: '/settings/vendor-api/verify-connection',
            method: 'POST',
            data: requestData,
          });
        }

        if (testOnly) {
          notifications.showNotification({
            message: 'Connection succeeded',
            type: 'success',
          });
        }
      } catch (e) {
        notifyVerifyError(e);
      }
    },
  );

  const addVendor = async (vendor: Vendor) => {
    const { testOnly, ...values } = vendor;
    const requestData = updateVendorForRequest(values);

    if (testOnly) {
      await verifyConnection(requestData, testOnly);
      return;
    }
    const data = {
      ...requestData,
      details: getVendorApiDetails(requestData),
      id: generateGuid(),
      unsaved: true,
    };

    props.onVendorsChange([
      ...props.vendors,
      { ...data, id: generateGuid(), unsaved: true },
    ]);
    setAddVendorDialogOpen(false);
  };

  const updateVendor = async (vendor: Vendor) => {
    const { testOnly, unsaved, id, ...values } = vendor;
    const requestData = updateVendorForRequest(values);

    if (testOnly) {
      await verifyConnection(
        {
          ...requestData,
          id: unsaved ? undefined : id,
        },
        testOnly,
      );
      return;
    }
    const data = {
      ...requestData,
      id: id!,
      unsaved,
      details: getVendorApiDetails(requestData),
    };

    const foundIndex = props.vendors.findIndex(
      (item) => typeof item?.id === 'string' && item.id === data.id,
    );
    const newVendors = [...props.vendors];
    newVendors[foundIndex] = data;
    props.onVendorsChange(newVendors);
    setEditVendor(null);
  };

  const handleSubmit = async (vendor: Vendor, type: VendorDialogType) => {
    await withLoadingIndicator(setTestingVendor)(async () => {
      if (type === 'add') {
        await addVendor(vendor);
      } else {
        await updateVendor(vendor);
      }
    });
  };

  const handleShowEdit = (id: string) => {
    setEditVendor(id);
  };

  const closeEditVendor = () => {
    setEditVendor(null);
  };

  const handleDelete = (ids: Set<string>) => {
    const newVendors = props.vendors.filter(
      (vendor) => typeof vendor?.id === 'string' && !ids.has(vendor.id),
    );
    props.onVendorsChange(newVendors);
  };

  const handleUseGlobalSettings = async () => {
    if (!props.newSnapshot && selectedSnapshot === null) {
      stumble('Snapshot is not selected and is not new.');
      return;
    }
    let overwriteSettings = true;
    if (!props.newSnapshot) {
      overwriteSettings = await useGlobalSettingsConfirmDialog.show({
        btnConfirm: 'Overwrite',
        btnSecondaryConfirm: 'Cancel',
        header: 'Update Vendors API from global settings',
        subHeader: 'Overwrite current settings?',
      });
    }
    if (!overwriteSettings) {
      return;
    }
    await withLoadingIndicator(setGlobalStateLoading)(async () => {
      try {
        const response = await api.request({
          url: `/snapshots/${selectedSnapshot}/settings/vendor-api-from-global`,
          method: 'POST',
        });
        props.onVendorsChange(response);
        notifications.showNotification({
          type: 'success',
          message: 'Vendor API settings saved.',
        });
      } catch (e: unknown) {
        notifications.showNotification({
          message:
            (isIpfError(e, 'API') && e?.payload?.response.message) ||
            'Unknown error',
          title: 'Fetching vendors from global settings failed.',
          type: 'danger',
        });
        throw e;
      }
    });
  };

  const handleOpenAddVendorDialog = () => {
    setAddVendorDialogOpen(true);
  };

  const handleCloseAddVendorDialog = () => {
    setAddVendorDialogOpen(false);
  };

  const handleVendorEnabledChange = (ids: Set<string>, isEnabled: boolean) => {
    const updatedVendors = props.vendors.map((vendor) => {
      if (vendor.id && ids.has(vendor.id)) {
        return {
          ...vendor,
          isEnabled,
        };
      }
      return vendor;
    });
    props.onVendorsChange(updatedVendors);
  };

  const renderVendorsLoadingContent = () => (
    <div className='flex flex-1 items-center justify-center'>
      <LoaderAndText />
    </div>
  );

  const hasVendors = props.vendors?.length > 0;

  const renderAddVendor = () => {
    if (addVendorDialogOpen) {
      return (
        <VendorDialog
          dialogType='add'
          onClose={handleCloseAddVendorDialog}
          onSubmit={(values) => handleSubmit(values, 'add')}
          testingVendor={testingVendor === 'LOADING'}
          type={settingTypes.vendorApiNames.meraki}
        />
      );
    }
    return null;
  };

  const renderVendorList = () => (
    <>
      {useGlobalSettingsConfirmDialog.render()}
      {renderAddVendor()}

      {editVendor !== null && hasVendors && (
        <VendorDialog
          dialogType='edit'
          editName={getEditName()}
          initialValues={getEditInitialValues()}
          onClose={closeEditVendor}
          onSubmit={handleSubmit}
          testingVendor={testingVendor === 'LOADING'}
          type={getEditType()}
        />
      )}

      <VendorsApiTable
        className='flex-1 -mx -mb -mt-4 rounded-b-lg bg-transparent'
        data={props.vendors}
        onAddVendor={handleOpenAddVendorDialog}
        onDeleteVendors={handleDelete}
        onEditVendor={handleShowEdit}
        onUseGlobalSettings={handleUseGlobalSettings}
        onVendorEnabledChange={handleVendorEnabledChange}
        showUpdateFromGlobalSettingsButton={
          props.showUpdateFromGlobalSettingsButton
        }
        titleEndAdornment={props.renderTitleBadge?.()}
        noCredentialBox={props.noCredentialBox}
      />
    </>
  );

  const renderContent = () => {
    if (globalStateLoading === 'LOADING') {
      return renderVendorsLoadingContent();
    }
    return renderVendorList();
  };

  return <div className='flex flex-1 flex-col'>{renderContent()}</div>;
};

export default VendorsApi;
